import { Autocomplete, AutocompleteProps, TextField, TextFieldProps } from '@mui/material';
import { Controller, Control } from 'react-hook-form';

type ControlledAutocompleteProps<T> = Omit<
  AutocompleteProps<T, false, false, false>,
  'renderInput' | 'value' | 'onChange'
> & {
  control: Control<any, any>;
  name: string;
  required?: boolean;
  errorMessage?: string;
  helperText?: string;
  label?: string;
  placeholder?: string;
  textFieldProps?: Partial<TextFieldProps>;
  getOptionValue?: (option: T) => any;
  multiple?: boolean;
};

export const ControlledAutocomplete = <T,>({
  name,
  label,
  control,
  defaultValue,
  options,
  getOptionLabel,
  getOptionValue,
  errorMessage,
  helperText,
  placeholder,
  textFieldProps,
  ...props
}: ControlledAutocompleteProps<T>) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue || null}
    render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
      const selectedOption = getOptionValue
        ? options.find((option) => getOptionValue(option) === value) || null
        : value || null;

      return (
        <Autocomplete
          {...props}
          options={options}
          getOptionLabel={getOptionLabel}
          value={selectedOption}
          onChange={(_, newValue) => {
            const valueToSubmit = getOptionValue && newValue ? getOptionValue(newValue) : newValue;
            onChange(valueToSubmit);
          }}
          onBlur={onBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              {...textFieldProps}
              label={label}
              placeholder={placeholder}
              variant="outlined"
              error={Boolean(error)}
              helperText={error ? errorMessage : helperText}
              inputRef={ref}
            />
          )}
        />
      );
    }}
  />
);
